import React from "react";
import { useApp } from "../context/AppContext";
import { securityClassMeta } from "../lib/portfolio";
import { AlertTriangle } from "lucide-react";

// Days-to-expiry status for option/bond holdings: "soon" (<=30d), "expired" (<0), or null.
export function expiryStatus(holding) {
  const type = (holding.type || "").toString().toLowerCase();
  if ((type !== "option" && type !== "bond") || !holding.expiration) return null;
  const exp = new Date(holding.expiration);
  if (Number.isNaN(exp.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((exp - today) / 86400000);
  if (days < 0) return { state: "expired", days };
  if (days <= 30) return { state: "soon", days };
  return null;
}

// Amber "expiring soon" / red "expired" pill for option & bond holdings.
export function ExpiryBadge({ holding }) {
  const { t } = useApp();
  const st = expiryStatus(holding);
  if (!st) return null;
  const expired = st.state === "expired";
  const color = expired ? "#EF4444" : "#F59E0B";
  return (
    <span data-testid="expiry-badge" title={holding.expiration}
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium"
      style={{ color, background: `${color}1A` }}>
      <AlertTriangle className="w-3 h-3" />
      {expired ? t("portfolio_expired") : `${t("portfolio_expiring_soon")} · ${st.days}${t("portfolio_days_short")}`}
    </span>
  );
}

// Small colored dot + label badge for a holding's asset class.
export function ClassBadge({ type }) {
  const { lang } = useApp();
  const meta = securityClassMeta(type);
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-text-secondary" data-testid={`class-badge-${meta.key}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
      {meta.label[lang] || meta.label.en}
    </span>
  );
}

// Contract detail sub-line for options (side · strike · expiry) and bonds (coupon · maturity).
export function HoldingMeta({ holding }) {
  const { t, lang } = useApp();
  const type = (holding.type || "").toString().toLowerCase();
  const fmtDate = (d) => {
    try {
      return new Date(d).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return d; }
  };
  const parts = [];
  if (type === "option") {
    if (holding.option_side) parts.push((holding.option_side || "").toUpperCase());
    if (holding.strike != null) parts.push(`${t("activity_strike")} ${holding.strike}`);
    if (holding.expiration) parts.push(`${t("portfolio_expiry")} ${fmtDate(holding.expiration)}`);
  } else if (type === "bond") {
    if (holding.coupon != null) parts.push(`${t("portfolio_coupon")} ${holding.coupon}%`);
    if (holding.expiration) parts.push(`${t("portfolio_maturity")} ${fmtDate(holding.expiration)}`);
  }
  if (!parts.length) return null;
  return (
    <div className="text-[10px] text-text-secondary mt-0.5" data-testid="holding-contract-meta">
      {parts.join(" · ")}
    </div>
  );
}
